'use client';
import React, { useState, useEffect, useRef } from 'react';

interface PrayerEntry {
  name: string;
  arabic: string;
  time: string;
  iqama?: string;
  isNext?: boolean;
  isCurrent?: boolean;
}

interface AladhanTimings {
  Fajr: string;
  Sunrise: string;
  Dhuhr: string;
  Asr: string;
  Maghrib: string;
  Isha: string;
}

function to12h(t: string): string {
  if (!t) return '--:--';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  let hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

function timeToMinutes(t: string): number {
  const clean = t.replace(/\s?(AM|PM)/i, '').trim();
  const [h, m] = clean.split(':').map(Number);
  const isPM = /PM/i.test(t);
  const isAM = /AM/i.test(t);
  let hour = h;
  if (isPM && h !== 12) hour += 12;
  if (isAM && h === 12) hour = 0;
  return hour * 60 + m;
}

export default function PrayerClock() {
  const [currentTime, setCurrentTime] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [hijriDate, setHijriDate] = useState('');
  const [activeTab, setActiveTab] = useState<'today' | 'monthly'>('today');
  const [prayers, setPrayers] = useState<PrayerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const blobRef1 = useRef<HTMLDivElement>(null);
  const blobRef2 = useRef<HTMLDivElement>(null);
  const blobRef3 = useRef<HTMLDivElement>(null);

  // Parallax blobs on scroll
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      if (blobRef1.current) blobRef1.current.style.transform = `translateY(${y * 0.12}px)`;
      if (blobRef2.current) blobRef2.current.style.transform = `translateY(${y * -0.08}px)`;
      if (blobRef3.current) blobRef3.current.style.transform = `translateY(${y * 0.06}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Live clock
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }));
      setCurrentDate(now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  // Hijri date
  useEffect(() => {
    const now = new Date();
    const hijriMonths = ['Muharram','Safar','Rabi al-Awwal','Rabi al-Thani','Jumada al-Awwal','Jumada al-Thani','Rajab',"Sha\'ban",'Ramadan','Shawwal',"Dhul Qi\'dah",'Dhul Hijjah'];
    const jd = Math.floor((now.getTime() / 86400000) + 2440587.5);
    const l = jd - 1948440 + 10632;
    const n = Math.floor((l - 1) / 10631);
    const ll = l - 10631 * n + 354;
    const j = Math.floor((10985 - ll) / 5316) * Math.floor((50 * ll) / 17719) + Math.floor(ll / 5670) * Math.floor((43 * ll) / 15238);
    const ll2 = ll - Math.floor((30 - j) / 15) * Math.floor((17719 * j) / 50) - Math.floor(j / 16) * Math.floor((15238 * j) / 43) + 29;
    const m = Math.floor((24 * ll2) / 709);
    const d = ll2 - Math.floor((709 * m) / 24);
    const y = 30 * n + j - 30;
    setHijriDate(`${d} ${hijriMonths[m - 1] || ''} ${y} AH`);
  }, []);

  // Fetch prayer times from Aladhan API — Muslim World League (method=3), Chicago
  useEffect(() => {
    const fetchTimes = async () => {
      try {
        const now = new Date();
        const dateStr = `${String(now.getDate()).padStart(2,'0')}-${String(now.getMonth()+1).padStart(2,'0')}-${now.getFullYear()}`;
        // method=3 = Muslim World League
        const res = await fetch(`https://api.aladhan.com/v1/timingsByCity/${dateStr}?city=Chicago&country=US&method=3&school=0`);
        const data = await res.json();
        if (data.code === 200 && data.data?.timings) {
          const t: AladhanTimings = data.data.timings;
          const now2 = new Date();
          const nowMin = now2.getHours() * 60 + now2.getMinutes();

          const rawPrayers = [
            { name: 'Fajr', arabic: 'الفجر', time: to12h(t.Fajr), iqama: '5:45 AM' },
            { name: 'Dhuhr', arabic: 'الظهر', time: to12h(t.Dhuhr), iqama: '1:30 PM' },
            { name: "1st Jumu\'ah", arabic: 'الجمعة', time: '1:00 PM', iqama: '1:15 PM' },
            { name: "2nd Jumu\'ah", arabic: 'الجمعة', time: '2:00 PM', iqama: '2:15 PM' },
            { name: 'Asr', arabic: 'العصر', time: to12h(t.Asr), iqama: '4:45 PM' },
            { name: 'Maghrib', arabic: 'المغرب', time: to12h(t.Maghrib), iqama: 'Sunset' },
            { name: 'Isha', arabic: 'العشاء', time: to12h(t.Isha), iqama: '9:00 PM' },
          ];

          // Determine next prayer
          let nextIdx = -1;
          for (let i = 0; i < rawPrayers.length; i++) {
            const p = rawPrayers[i];
            if (p.name === "Jumu'ah") continue; // skip jummah for next calc
            const pMin = timeToMinutes(p.time);
            if (pMin > nowMin) { nextIdx = i; break; }
          }

          const result: PrayerEntry[] = rawPrayers.map((p, i) => ({
            ...p,
            isNext: i === nextIdx,
          }));
          setPrayers(result);
        }
      } catch {
        // Fallback static times
        setPrayers([
          { name: 'Fajr', arabic: 'الفجر', time: '5:30 AM', iqama: '5:45 AM' },
          { name: 'Dhuhr', arabic: 'الظهر', time: '1:15 PM', iqama: '1:30 PM' },
          { name: "1st Jumu'ah", arabic: 'الجمعة', time: '1:00 PM', iqama: '1:15 PM' },
          { name: "2nd Jumu'ah", arabic: 'الجمعة', time: '2:00 PM', iqama: '2:15 PM' },
          { name: 'Asr', arabic: 'العصر', time: '4:30 PM', iqama: '4:45 PM' },
          { name: 'Maghrib', arabic: 'المغرب', time: 'Sunset', iqama: 'Sunset' },
          { name: 'Isha', arabic: 'العشاء', time: '8:45 PM', iqama: '9:00 PM' },
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchTimes();
  }, []);

  const timeParts = currentTime ? currentTime.match(/^(\d+:\d+:\d+)\s*(AM|PM)$/) : null;
  const timeMain = timeParts ? timeParts[1] : '--:--:--';
  const timeAmpm = timeParts ? timeParts[2] : '';

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Amiri:ital,wght@0,400;0,700;1,400&display=swap');

        /* ── Full-width Banner Wrapper ── */
        .pc-banner {
          position: relative;
          width: 100%;
          overflow: hidden;
          background: #060d12;
        }

        /* ── Layered Background ── */
        .pc-bg-base {
          position: absolute;
          inset: 0;
          background: linear-gradient(160deg, #060d12 0%, #0a1628 40%, #060d12 70%, #080f1a 100%);
        }
        .pc-bg-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(59,130,246,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(59,130,246,0.04) 1px, transparent 1px);
          background-size: 48px 48px;
          pointer-events: none;
        }
        .pc-bg-grain {
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
          opacity: 0.5;
          pointer-events: none;
        }

        /* ── Parallax Blobs ── */
        .pc-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
          will-change: transform;
        }
        .pc-blob-1 {
          width: 55vw; height: 55vw;
          max-width: 700px; max-height: 700px;
          top: -20%; right: -10%;
          background: radial-gradient(circle, rgba(30,58,138,0.35) 0%, transparent 70%);
        }
        .pc-blob-2 {
          width: 40vw; height: 40vw;
          max-width: 500px; max-height: 500px;
          bottom: -15%; left: -8%;
          background: radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%);
        }
        .pc-blob-3 {
          width: 30vw; height: 30vw;
          max-width: 400px; max-height: 400px;
          top: 30%; left: 40%;
          background: radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 70%);
        }

        /* ── Geometric Ring ── */
        .pc-ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(59,130,246,0.08);
          pointer-events: none;
        }
        .pc-ring-1 { width: 500px; height: 500px; top: -200px; right: -150px; }
        .pc-ring-2 { width: 300px; height: 300px; top: -80px; right: -20px; border-color: rgba(212,175,55,0.06); }

        /* ── Hero Clock Section ── */
        .pc-hero {
          position: relative;
          z-index: 2;
          padding: 64px 48px 48px;
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 48px;
          align-items: center;
          max-width: 1320px;
          margin: 0 auto;
        }

        /* Left: Bismillah + Clock */
        .pc-hero-left {}
        .pc-bismillah {
          font-family: 'Amiri', serif;
          font-size: 22px;
          color: rgba(147,197,253,0.3);
          direction: rtl;
          display: block;
          margin-bottom: 20px;
        }
        .pc-label {
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.2);
          margin-bottom: 12px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .pc-label::before {
          content: '';
          display: inline-block;
          width: 20px; height: 1px;
          background: rgba(255,255,255,0.12);
        }

        /* Secondary: current time */
        .pc-current-time-label {
          font-family: monospace;
          font-size: 8px;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: rgba(147,197,253,0.3);
          margin-bottom: 6px;
        }
        .pc-current-time {
          font-family: 'DM Sans', sans-serif;
          font-size: clamp(28px, 4vw, 44px);
          font-weight: 300;
          color: rgba(255,255,255,0.55);
          letter-spacing: -0.02em;
          line-height: 1;
          margin-bottom: 4px;
        }
        .pc-current-time-ampm {
          font-size: 0.45em;
          font-weight: 400;
          color: rgba(147,197,253,0.4);
          vertical-align: super;
          margin-left: 3px;
        }
        .pc-date {
          font-family: 'DM Sans', sans-serif;
          font-size: 13px;
          color: rgba(147,197,253,0.35);
          letter-spacing: 0.04em;
          margin-top: 6px;
        }
        .pc-hijri {
          font-family: 'Amiri', serif;
          font-size: 14px;
          color: rgba(147,197,253,0.25);
          margin-top: 3px;
        }

        /* Right: Masjid info */
        .pc-hero-right {
          text-align: right;
        }
        .pc-masjid-name {
          font-family: 'Amiri', serif;
          font-size: clamp(18px, 2.5vw, 26px);
          font-weight: 700;
          color: rgba(255,255,255,0.7);
          margin-bottom: 4px;
        }
        .pc-masjid-sub {
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(147,197,253,0.3);
        }
        .pc-method-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 12px;
          padding: 6px 12px;
          background: rgba(59,130,246,0.08);
          border: 1px solid rgba(59,130,246,0.15);
          font-family: monospace;
          font-size: 8px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(147,197,253,0.5);
        }
        .pc-method-dot {
          width: 5px; height: 5px;
          border-radius: 50%;
          background: #3b82f6;
          animation: pc-pulse 2s infinite;
        }
        @keyframes pc-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        /* ── Divider ── */
        .pc-divider {
          position: relative;
          z-index: 2;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(59,130,246,0.2), rgba(212,175,55,0.15), rgba(59,130,246,0.2), transparent);
          margin: 0 48px;
        }

        /* ── Tabs ── */
        .pc-tabs {
          position: relative;
          z-index: 2;
          display: flex;
          border-bottom: 1px solid rgba(59,130,246,0.1);
          background: rgba(6,13,18,0.6);
          max-width: 1320px;
          margin: 0 auto;
          padding: 0 48px;
        }
        .pc-tab {
          padding: 14px 20px;
          font-family: monospace;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: rgba(148,163,184,0.4);
          background: none;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
        }
        .pc-tab.active {
          color: #93c5fd;
          border-bottom-color: #3b82f6;
        }
        .pc-tab:hover:not(.active) { color: rgba(148,163,184,0.7); }

        /* ── Prayer Rows ── */
        .pc-prayers {
          position: relative;
          z-index: 2;
          max-width: 1320px;
          margin: 0 auto;
          padding: 0 48px 48px;
        }
        .pc-prayer-header {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          padding: 12px 20px;
          background: rgba(59,130,246,0.05);
          border-bottom: 1px solid rgba(59,130,246,0.1);
          margin-top: 24px;
        }
        .pc-prayer-header span {
          font-family: monospace;
          font-size: 8px;
          font-weight: 700;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: rgba(147,197,253,0.3);
        }
        .pc-prayer-header span:nth-child(2) { text-align: center; }
        .pc-prayer-header span:last-child { text-align: right; }

        .pc-prayer-row {
          display: grid;
          grid-template-columns: 2fr 1fr 1fr;
          align-items: center;
          padding: 20px 20px;
          border-bottom: 1px solid rgba(59,130,246,0.05);
          transition: background 0.2s;
          position: relative;
        }
        .pc-prayer-row:last-child { border-bottom: none; }
        .pc-prayer-row:hover { background: rgba(59,130,246,0.03); }

        .pc-prayer-row.is-next {
          background: rgba(59,130,246,0.08);
          border-left: 3px solid #3b82f6;
        }
        .pc-prayer-row.is-jummah {
          background: rgba(212,175,55,0.03);
          border-left: 3px solid rgba(212,175,55,0.3);
        }

        .pc-next-badge {
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
          font-family: monospace;
          font-size: 7px;
          font-weight: 700;
          letter-spacing: 0.2em;
          color: #3b82f6;
          background: rgba(59,130,246,0.12);
          padding: 3px 8px;
          border: 1px solid rgba(59,130,246,0.2);
        }

        .pc-prayer-name-col {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .pc-prayer-name-en {
          font-family: 'DM Sans', sans-serif;
          font-size: clamp(16px, 2vw, 22px);
          font-weight: 600;
          color: #e2e8f0;
          line-height: 1;
        }
        .pc-prayer-name-ar {
          font-family: 'Amiri', serif;
          font-size: 15px;
          color: rgba(147,197,253,0.3);
          direction: rtl;
          line-height: 1;
        }
        .pc-prayer-row.is-jummah .pc-prayer-name-en {
          color: rgba(212,175,55,0.85);
        }
        .pc-prayer-row.is-jummah .pc-prayer-name-ar {
          color: rgba(212,175,55,0.3);
        }

        .pc-adhan-time {
          font-family: 'DM Sans', sans-serif;
          font-size: clamp(18px, 2.5vw, 26px);
          font-weight: 500;
          color: #93c5fd;
          letter-spacing: 0.02em;
          text-align: center;
        }
        .pc-prayer-row.is-jummah .pc-adhan-time {
          color: rgba(212,175,55,0.8);
        }
        .pc-iqama-time {
          font-family: 'DM Sans', sans-serif;
          font-size: clamp(14px, 1.8vw, 18px);
          font-weight: 400;
          color: rgba(148,163,184,0.4);
          text-align: right;
          letter-spacing: 0.02em;
        }
        .pc-iqama-time.has-iqama {
          color: rgba(148,163,184,0.6);
        }

        /* Loading */
        .pc-loading {
          padding: 40px;
          text-align: center;
          font-family: monospace;
          font-size: 10px;
          letter-spacing: 0.2em;
          color: rgba(147,197,253,0.3);
        }

        /* ── Monthly iframe ── */
        .pc-monthly {
          position: relative;
          z-index: 2;
          max-width: 1320px;
          margin: 0 auto;
          padding: 24px 48px 48px;
        }
        .pc-monthly iframe {
          display: block;
          width: 100%;
          border: none;
          filter: invert(0.88) hue-rotate(180deg) brightness(0.9) saturate(0.85);
        }

        /* ── Footer ── */
        .pc-footer {
          position: relative;
          z-index: 2;
          border-top: 1px solid rgba(59,130,246,0.08);
          padding: 14px 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(6,13,18,0.5);
        }
        .pc-footer-text {
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.12em;
          color: rgba(148,163,184,0.3);
        }
        .pc-footer-link {
          font-family: monospace;
          font-size: 9px;
          letter-spacing: 0.12em;
          color: rgba(147,197,253,0.4);
          text-decoration: none;
          transition: color 0.2s;
        }
        .pc-footer-link:hover { color: #93c5fd; }

        @media (max-width: 900px) {
          .pc-hero { grid-template-columns: 1fr; gap: 24px; padding: 40px 24px 32px; }
          .pc-hero-right { text-align: left; }
          .pc-divider { margin: 0 24px; }
          .pc-tabs { padding: 0 24px; }
          .pc-prayers { padding: 0 24px 36px; }
          .pc-monthly { padding: 16px 24px 36px; }
          .pc-footer { padding: 12px 24px; flex-direction: column; gap: 6px; text-align: center; }
        }
        @media (max-width: 640px) {
          .pc-prayer-row { padding: 16px 14px; }
          .pc-prayer-header { padding: 10px 14px; }
        }
      `}</style>

      <div className="pc-banner">
        {/* Layered Background */}
        <div className="pc-bg-base" />
        <div className="pc-bg-grid" />
        <div className="pc-bg-grain" />

        {/* Parallax Blobs */}
        <div className="pc-blob pc-blob-1" ref={blobRef1} />
        <div className="pc-blob pc-blob-2" ref={blobRef2} />
        <div className="pc-blob pc-blob-3" ref={blobRef3} />

        {/* Geometric Rings */}
        <div className="pc-ring pc-ring-1" />
        <div className="pc-ring pc-ring-2" />

        {/* Hero Section */}
        <div className="pc-hero">
          <div className="pc-hero-left">
            <span className="pc-bismillah">بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ</span>
            <div className="pc-label">Prayer Times — Masjid Al-Ihsan</div>
            <div className="pc-current-time-label">Current Time</div>
            <div className="pc-current-time">
              {timeMain}
              {timeAmpm && <span className="pc-current-time-ampm">{timeAmpm}</span>}
            </div>
            <div className="pc-date">{currentDate || 'Loading...'}</div>
            <div className="pc-hijri">{hijriDate}</div>
          </div>
          <div className="pc-hero-right">
            <div className="pc-masjid-name">Masjid Al-Ihsan</div>
            <div className="pc-masjid-sub">Bronzeville · Chicago, IL</div>
            <div className="pc-method-badge">
              <div className="pc-method-dot" />
              Muslim World League Calculation
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="pc-divider" />

        {/* Tabs */}
        <div className="pc-tabs">
          <button className={`pc-tab ${activeTab === 'today' ? 'active' : ''}`} onClick={() => setActiveTab('today')}>
            Today&apos;s Prayers
          </button>
          <button className={`pc-tab ${activeTab === 'monthly' ? 'active' : ''}`} onClick={() => setActiveTab('monthly')}>
            Monthly Timetable
          </button>
        </div>

        {/* Today's Prayers */}
        {activeTab === 'today' && (
          <div className="pc-prayers">
            {loading ? (
              <div className="pc-loading">Loading prayer times...</div>
            ) : (
              <>
                <div className="pc-prayer-header">
                  <span>Prayer</span>
                  <span>Adhan</span>
                  <span>Iqama</span>
                </div>
                {prayers.map((p, i) => (
                  <div
                    key={i}
                    className={`pc-prayer-row ${p.isNext ? 'is-next' : ''} ${p.name === "Jumu'ah" ? 'is-jummah' : ''}`}
                  >
                    <div className="pc-prayer-name-col">
                      <span className="pc-prayer-name-en">{p.name}</span>
                      <span className="pc-prayer-name-ar">{p.arabic}</span>
                    </div>
                    <span className="pc-adhan-time">{p.time}</span>
                    <span className={`pc-iqama-time ${p.iqama ? 'has-iqama' : ''}`}>
                      {p.iqama ?? '—'}
                    </span>
                    {p.isNext && <span className="pc-next-badge">NEXT</span>}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Monthly Timetable */}
        {activeTab === 'monthly' && (
          <div className="pc-monthly">
            <iframe
              src="https://timing.athanplus.com/masjid/widgets/embed?theme=1&masjid_id=oxdN0YLV"
              width="100%"
              height="680"
              title="Monthly Prayer Timetable"
            />
          </div>
        )}

        {/* Footer */}
        <div className="pc-footer">
          <span className="pc-footer-text">Muslim World League · Aladhan API · Chicago, IL 60653</span>
          <a
            href="https://masjidal.com/widget/monthly/v2?masjid_id=oxdN0YLV"
            target="_blank"
            rel="noopener noreferrer"
            className="pc-footer-link"
          >
            ↓ Download Monthly PDF
          </a>
        </div>
      </div>
    </>
  );
}
